import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import type { Message } from '@dev-streetbeat/chat';

import PortfolioConfigData from "config/portfolio-config.json";
import { bodyEmail } from "data/body-email";
import { useToast } from "shared/components/ui/use-toast";
import { AssistantService } from "shared/services/AssistantService";
import useThreadsStore from "shared/store/threadsStore";
import { createAssistantMessage } from "utils/assistantUtils";
import {
  extractPortfolioFromArtifact,
  isPortfolioStoryArtifact
} from "utils/portfolioUtils";
import { FinecoPortfolioStoryArtifact } from "../../fineco-portfolio-story-artifact/FinecoPortfolioStoryArtifact";
import { FinecoPortfolioButtons } from "./components/FinecoPortfolioButtons";
import FinecoPortfolioTitle from "../components/FinecoPortfolioTitle";
import { Section } from "../components/Section";
import AnalisiRischioRendimentoAttesoContainer from "../components/analisi-rischio-e-rendimento-atteso/AnalisiRischioRendimentoAttesoContainer";
import AnalisiStrumentiContainer from "../components/analisi-strumenti/AnalisiStrumentiContainer";
import AssetAllocationImplicitaContainer from "../components/asset-allocation-implicita/AssetAllocationImplicitaContainer";
import CaratteristicheDiPortafoglioContainer from "../components/caratteristiche-di-portafoglio/CaratteristicheDiPortafoglioContainer";
import CommentoContainer from "../components/commento/CommentoContainer";
import ComposizioneDelPortafoglioContainer from "../components/composizione-del-portafoglio/ComposizioneDelPortafoglioContainer";
import DistribuzioneTipologiaContainer from "../components/DistribuzioneTipologiaContainer";
import DistribuzioneDeiProventiContainer from "../components/distribuzione-proventi/DistribuzioneDeiProventiContainer";
import EfficientFrontierContainer from "../components/frontiera-efficiente/EfficientFrontierContainer";
import IndicatoriFinanziariContainer from "../components/indicatori-finanziari/IndicatoriFinanziariContainer";
import ScomposizionePortafoglioContainer from "../components/scomposizione-di-portafoglio/ScomposizionePortafolioContainer";
import DistribuzioneEsgContainer from "../components/sostenibilita/components/DistribuzioneEsgContainer";
import { ef_data } from "../FinecoConfigData";
import BacktestContainer from "../components/backtest/BacktestContainer";

import styles from "pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss";

type Props = {
  artifact: any;
  message: Message & { thread_id: string };
  finecoPortfolioAndPortfolioStoryTogether: boolean;
  onLoaded?: () => void;
};

// Memoize portfolio story component to prevent unnecessary re-renders
const MemoizedPortfolioStory = memo(
  ({
    message,
    onLoaded,
  }: {
    message: Message & { thread_id: string };
    onLoaded?: () => void;
  }) => (
    <>
      {message.artifacts?.map((artifact: any) => {
        if (isPortfolioStoryArtifact(artifact)) {
          return (
            <FinecoPortfolioStoryArtifact
              key={Math.random()}
              data={artifact.custom_data.data}
              onLoaded={onLoaded}
            />
          );
        }
        return null;
      })}
    </>
  ),
);

MemoizedPortfolioStory.displayName = "MemoizedPortfolioStory";

export const FinecoPortfolio: React.FC<Props> = memo(
  ({ artifact, message, onLoaded }) => {
    const appendMessages = useThreadsStore((state) => state.appendMessages);
    const { t } = useTranslation();
    const { toast } = useToast();
    const params = useParams();
    const threadId = params.threadId;
    const [reportData, setReportData] = useState<any>(null);
    const [generatingPdf, setGeneratingPdf] = useState<boolean>(false);
    const [generatingDownloadPdf, setGeneratingDownloadPdf] =
      useState<boolean>(false);
    const [generatingExcel, setGeneratingExcel] = useState<boolean>(false);
    const generateReportRef = useRef(null);

    // Extract data using shared helper
    const { portfolioData, portfolioId, commentoAlPortafoglio } = extractPortfolioFromArtifact(artifact);

    // Access story from global store
    const stories = useThreadsStore((state) => state.stories);
    const fetchStoryLocal = useThreadsStore((state) => state.fetchStory);

    // Helper to ensure story is available when needed (only if CommentoContainer is not already handling it)
    const ensureStoryAvailable = useCallback(
      async (iterations: number = 12) => {
        if (iterations <= 0 || !threadId || !portfolioId) return;

        const currentStory = stories?.[threadId]?.[portfolioId];
        if (currentStory === null || currentStory === undefined) {
          fetchStoryLocal(threadId, portfolioId);
          setTimeout(() => ensureStoryAvailable(iterations - 1), 5000);
        }
      },
      [threadId, portfolioId, stories, fetchStoryLocal],
    );

    const sectionsConfig = useMemo(
      () => ({
        efficientFrontierContainer: {
          label: "",
          component: (
            <EfficientFrontierContainer ef_data={ef_data} reportData={reportData} />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        statsContainer: {
          label: "",
          component: (
            <CaratteristicheDiPortafoglioContainer
              data={portfolioData}
              label={PortfolioConfigData.stats.label}
            />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        indicatoriFinanziariContainer: {
          label: "",
          component: (
            <IndicatoriFinanziariContainer
              reportData={reportData}
              label={PortfolioConfigData.indicatori_finanziari.label}
            />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        distribuzioneTipologiaStrumentiContainer: {
          label: "",
          component: (
            <DistribuzioneTipologiaContainer reportData={reportData} />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        portfolio: {
          label: PortfolioConfigData.portfolio.label,
          component: (
            <ComposizioneDelPortafoglioContainer reportData={reportData} />
          ),
          isOpen: true,
          showFoldingTitle: false,
        },
        caratteristicaDiPortafoglio: {
          label: PortfolioConfigData.caratteristiche_di_portafoglio.label,
          component: (
            <DistribuzioneDeiProventiContainer reportData={reportData} />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        assetAllocationImplicita: {
          label: PortfolioConfigData.implicit_asset_allocation.label,
          component: (
            <AssetAllocationImplicitaContainer reportData={reportData} />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        scomposizionePortafoglio: {
          label: PortfolioConfigData.scomposizione_portafoglio.label,
          component: (
            <ScomposizionePortafoglioContainer reportData={reportData} />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        analisi_rischio_rendimento_atteso: {
          label: PortfolioConfigData.analisi_rischio_rendimento_atteso.label,
          component: (
            <AnalisiRischioRendimentoAttesoContainer reportData={reportData} />
          ),
          isOpen: false,
          showFoldingTitle: true,
        },
        backtestContainer: {
          label: PortfolioConfigData.backtest.label,
          component: <BacktestContainer reportData={reportData} />,
          isOpen: false,
          showFoldingTitle: true,
        },
        analisi_strumenti: {
          label: PortfolioConfigData.analisi_strumenti.label,
          component: <AnalisiStrumentiContainer reportData={reportData} />,
          isOpen: false,
          showFoldingTitle: true,
        },
        distribuzioneEsg: {
          label: "Sostenibilità",
          component: <DistribuzioneEsgContainer reportData={reportData} />,
          isOpen: false,
          showFoldingTitle: true,
        },
        ...(commentoAlPortafoglio === true && {
          portfolioStory: {
            label: "Commento al portafoglio",
            component: (
              <CommentoContainer
                threadId={threadId}
                portfolioId={portfolioId}
              />
            ),
            isOpen: false,
            showFoldingTitle: true,
          },
        }),
      }),
      [reportData, artifact, message, onLoaded, ef_data],
    );

    const [openSections, setOpenSections] = useState(() =>
      Object.fromEntries(
        Object.entries(sectionsConfig).map(([key, { isOpen }]) => [
          key,
          isOpen,
        ]),
      ),
    );

    const toggleSection = useCallback((section: string) => {
      setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
    }, []);

    // Memoize PDF generation callback
    const requestGeneratePdf = useCallback(
      async (reportSpec: any, fromReport: boolean = false) => {
        if (fromReport) {
          setGeneratingPdf(true);
        }

        try {
          let rawPdf;
          let story = "";

          // Prefer v2 API with portfolio data, fallback to v1 with thread/portfolio IDs
          if (portfolioData) {
            // Check if portfolio_story section is selected in reportSpec
            const isPortfolioStorySelected = reportSpec?.report_sections?.portfolio_story?.visible &&
              reportSpec?.report_sections?.portfolio_story?.selected;

            // Only get story from global state if portfolio_story is selected
            if (isPortfolioStorySelected && threadId && portfolioId) {
              const currentStories = useThreadsStore.getState().stories;
              story = currentStories?.[threadId]?.[portfolioId] || "";

              // If story is not available and CommentoContainer is not rendered, fetch it
              if (!story && !commentoAlPortafoglio) {
                ensureStoryAvailable();
                // Use empty story for now, user will need to regenerate when story is ready
                story = "";
              }
            } else {
              // Portfolio story not selected, set story to empty string
              story = "";
            }

            // Show toast for download start
            toast({
              title: t("generateReport.downloading"),
              description: t("generateReport.preparingPdf"),
            });

            rawPdf = await AssistantService.createPdf(portfolioId, reportSpec);
          } else {
            throw new Error("No portfolio data or thread/portfolio IDs available for PDF generation");
          }

          const blob = new Blob([rawPdf], { type: "application/pdf" });
          if (blob.type === "application/pdf") {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `portfolio_${portfolioId || 'report'}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Show success toast
            toast({
              title: t("generateReport.downloadComplete"),
              description: t("generateReport.pdfReady"),
            });

            // Add assistant message if in thread context
            if (threadId) {
              const assistantMessage = createAssistantMessage(
                t("assistant.reportDownloaded"),
                threadId,
              );
              appendMessages(threadId, [assistantMessage]);
            }
          } else {
            // Show error toast
            toast({
              title: t("generateReport.downloadError"),
              description: t("generateReport.pdfError"),
              variant: "destructive",
            });

            // Add assistant message if in thread context
            if (threadId) {
              const assistantMessage = createAssistantMessage(
                t("assistant.reportDownloadedError"),
                threadId,
              );
              appendMessages(threadId, [assistantMessage]);
            }
          }
        } catch (error) {
          console.error("Error generating PDF", error);
          if (threadId) {
            const assistantMessage = createAssistantMessage(
              t("assistant.reportDownloadedError"),
              threadId,
            );
            appendMessages(threadId, [assistantMessage]);
          }
        } finally {
          setGeneratingPdf(false);
          setGeneratingDownloadPdf(false);
        }
      },
      [threadId, portfolioId, portfolioData, appendMessages, t],
    );

    // Memoize Excel generation callback
    const requestGenerateExcel = useCallback(async () => {
      setGeneratingExcel(true);
      try {
        let response;

        // Show toast for download start
        toast({
          title: t("generateReport.downloading"),
          description: t("generateReport.preparingExcel"),
        });

        // Prefer v2 API with portfolio data, fallback to v1 with thread/portfolio IDs
        if (portfolioData) {
          response = await AssistantService.createExcel(portfolioId);
        } else {
          throw new Error("No portfolio data or thread/portfolio IDs available for Excel generation");
        }

        const filename = `portfolio_${portfolioId || 'report'}.xlsx`;
        const blob = new Blob([response.data || response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);

        // Show success toast
        toast({
          title: t("generateReport.downloadComplete"),
          description: t("generateReport.excelReady"),
        });

        // Add assistant message if in thread context
        if (threadId) {
          const assistantMessage = createAssistantMessage(
            t("assistant.excelDownloaded"),
            threadId,
          );
          appendMessages(threadId, [assistantMessage]);
        }
      } catch (error) {
        console.error("Error generating Excel", error);
        // Show error toast
        toast({
          title: t("generateReport.downloadError"),
          description: t("generateReport.excelError"),
          variant: "destructive",
        });

        // Add assistant message if in thread context
        if (threadId) {
          const assistantMessage = createAssistantMessage(
            t("assistant.excelDownloadedError"),
            threadId,
          );
          appendMessages(threadId, [assistantMessage]);
        }
      } finally {
        setGeneratingExcel(false);
      }
    }, [threadId, portfolioId, portfolioData, appendMessages, t]);

    // Memoize email handler
    const handleEmailClick = useCallback(() => {
      const subject = "Report di analisi del portafoglio";
      const body = bodyEmail;

      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }, []);

    const handleSavePortfolio = useCallback(() => {
      const savePortfolio = async () => {
        try {
          let data;

          // Prefer v2 API with portfolio data, fallback to v1 with thread/portfolio IDs
          if (portfolioData) {
            data = await AssistantService.saveInMyPortfolio(portfolioId);
          } else {
            throw new Error("No portfolio data or thread/portfolio IDs available for saving portfolio");
          }

          if (data?.url) {
            window.open(data.url, "_blank");
          } else {
            toast({
              title: t("global.error"),
              description: t("assistant.portfolioSaveUrlError"),
            });
          }
        } catch (error) {
          console.error("Error getting save portfolio URL:", error);
          toast({
            title: t("global.error"),
            description: t("assistant.portfolioSaveUrlError"),
          });
        }
      };

      savePortfolio();
    }, [threadId, portfolioId, portfolioData, toast, t]);

    useEffect(() => {
      const fetchData = async () => {
        if (portfolioData) {
          try {
            const data = await AssistantService.fetchReportData(portfolioId);
            setReportData(data);
          } catch (error) {
            console.error("Error fetching report data:", error);
          }
        }
      };

      fetchData();
      onLoaded?.();
    }, [portfolioData, onLoaded]);
    if (!reportData) {
      return (
        <div className={styles.loadingPortfolio}>
          {t("portfolio.loadingPortfolio")}
        </div>
      );
    }

    return (
      <div className={styles.portfolioMainContainer}>
        <div className={styles.portfolioContainer}>
          <div className={styles.portfolioContainerInner}>
            <div className={styles.portfolioContainerInnerInner}>
              <FinecoPortfolioTitle title={t("portfolio.PortfolioTitle")} />

              {/* START sections */}
              {Object.entries(sectionsConfig).map(
                ([sectionKey, { label, component, showFoldingTitle }]) => {
                  if (component.props.children) {
                    const shouldRender = React.Children.toArray(
                      component.props.children,
                    ).some((child) => child !== null);
                    if (!shouldRender) return null;
                  }

                  return (
                    <div key={sectionKey} className={styles.sectionContainer}>
                      <Section
                        showFoldingTitle={showFoldingTitle}
                        toggleSection={toggleSection}
                        openSections={openSections}
                        reportData={reportData}
                        label={label}
                        section={sectionKey}
                      >
                        <div className={styles.sectionComponentContainer}>
                          {component}
                        </div>
                      </Section>
                    </div>
                  );
                },
              )}
              {/* END sections */}
            </div>

            <FinecoPortfolioButtons
              generatingPdf={generatingPdf}
              generatingExcel={generatingExcel}
              generatingDownloadPdf={generatingDownloadPdf}
              requestGeneratePdfAction={requestGeneratePdf}
              requestGenerateExcel={requestGenerateExcel}
              handleSavePortfolio={handleSavePortfolio}
              handleEmailClick={handleEmailClick}
              setGeneratingDownloadPdf={setGeneratingDownloadPdf}
              generateReportRef={generateReportRef}
              artifact={artifact}
              threadId={threadId}
              portfolioId={portfolioId}
              portfolioData={portfolioData}
            />
          </div>
        </div>
        <div
          id={`lastAdvisorMessageFinecoPortfolio${portfolioId}`}
          className={styles.bottom}
        />
      </div>
    );
  },
  (prev, next) =>
    prev.artifact === next.artifact &&
    prev.message === next.message &&
    prev.onLoaded === next.onLoaded,
);

FinecoPortfolio.displayName = "FinecoPortfolioArtifact";

export default FinecoPortfolio;
